import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, RefreshCw } from 'lucide-react';
import { useAppointmentSlotsOperations } from '@/hooks/appointments/useAppointmentSlotsOperations.ts';
import UnifiedHeader from '@/components/common/UnifiedHeader';
import AppointmentSlotsSearchForm from './AppointmentSlotsSearchForm';
import AppointmentSlotsTableComponent from './AppointmentSlotsTableComponent';
import CreateTimeSlotsDialogComponent from './CreateTimeSlotsDialogComponent';
import AppointmentSlotsDialogsComponent from './AppointmentSlotsDialogsComponent';

interface ManageClinicAppointmentSlotsRefactoredProps {
  onBack: () => void;
}

const ManageClinicAppointmentSlotsRefactored: React.FC<ManageClinicAppointmentSlotsRefactoredProps> = ({ onBack }) => {
  const operations = useAppointmentSlotsOperations();

  // Initialize data on component mount - run only once
  useEffect(() => {
    operations.fetchClinics();
    // Load all appointments initially (without any filter)
    operations.fetchAppointments(0, true);
  }, []); // Empty dependency array to run only once on mount

  // Note: Appointment refetching is now handled manually through user actions
  // (clinic selection, search, reload) rather than automatic useEffect dependencies

  // Note: Facility fetching is now handled directly in handleClinicChange
  // to support both single clinic and "All Clinics" scenarios

  // Fetch dialog facilities when dialog clinic is selected
  useEffect(() => {
    operations.fetchDialogFacilities(operations.dialogClinicId);
  }, [operations.dialogClinicId, operations.fetchDialogFacilities]);

  // Filter clinics for dialog search
  const filteredClinicsForDialog = operations.clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(operations.clinicSearchQuery.toLowerCase())
  );

  const handleOpenCreateDialog = () => {
    // Reset form state when opening dialog
    operations.resetDialogState();
    operations.setIsDialogOpen(true);
  };

  const handleValidationErrorsClear = (field: string) => {
    const newErrors = { ...operations.validationErrors };
    delete newErrors[field];
    operations.setValidationErrors(newErrors);
  };

  // API-based filtering is now handled in the operations layer
  // Just apply facility filtering locally if needed (when "All Facilities" is not selected)
  const getDisplayedAppointments = () => {
    let displayedAppointments = operations.appointments;

    // Only apply local facility filtering if:
    // 1. A specific facility is selected (not "all")
    // 2. No advanced time slot filtering is applied (isFilterApplied is false)
    // When time slot filtering is applied, the API already returns filtered data
    if (!operations.isFilterApplied && operations.selectedFacility && operations.selectedFacility !== "all") {
      displayedAppointments = displayedAppointments.filter(appointment =>
        appointment.facility_name === operations.selectedFacility
      );
    }

    return displayedAppointments;
  };

  const displayedAppointments = getDisplayedAppointments();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      {/* Unified Header with Add Slots Button */}
      <UnifiedHeader
        title="Manage Clinic Appointment Slots"
        showBackButton={true}
        onBackClick={onBack}
        userType="PA"
        hideSearchButton={true}
        hideTimer={true}
        showAddSlotsButton={true}
        onAddSlotsClick={handleOpenCreateDialog}
      />

      {/* Search Form */}
      <AppointmentSlotsSearchForm
        clinics={operations.clinics}
        facilities={operations.facilities}
        selectedClinic={operations.selectedClinic}
        selectedFacility={operations.selectedFacility}
        facilitySearchQuery={operations.facilitySearchQuery}
        filterClinicSearchQuery={operations.filterClinicSearchQuery}
        isLoadingFacilities={operations.isLoadingFacilities}
        isFilterApplied={operations.isFilterApplied}
        isLoading={operations.isLoading}
        filteredAppointmentsCount={displayedAppointments.length}
        onClinicChange={operations.handleClinicChange}
        onFacilityChange={operations.handleFacilityChange}
        onFacilitySearchChange={operations.setFacilitySearchQuery}
        onFilterClinicSearchChange={operations.setFilterClinicSearchQuery}
        onOpenFilterDialog={() => operations.setIsFilterDialogOpen(true)}
        onDeleteAll={() => operations.handleDeleteAll(displayedAppointments)}
        onResetFilters={operations.handleResetFilters}
      />

      {/* Main Content - Table */}
      <main className="p-8 max-w-7xl mx-auto">
        <AppointmentSlotsTableComponent
          appointments={displayedAppointments}
          isLoading={operations.isLoading}
          currentPage={operations.currentPage}
          rowsPerPage={operations.rowsPerPage}
          hasMoreData={operations.hasMoreData}
          totalRecords={operations.totalCount}
          onDeleteAppointment={operations.handleDeleteAppointment}
          onRowsPerPageChange={operations.handleRowsPerPageChange}
          onPageChange={operations.handlePageChange}
        />
      </main>

      {/* Create Time Slot Dialog */}
      <CreateTimeSlotsDialogComponent
        isOpen={operations.isDialogOpen}
        clinics={filteredClinicsForDialog}
        dialogFacilities={operations.dialogFacilities}
        dialogClinic={operations.dialogClinic}
        selectedDialogFacility={operations.selectedDialogFacility}
        selectedDays={operations.selectedDays}
        startDate={operations.startDate}
        endDate={operations.endDate}
        startTime={operations.startTime}
        endTime={operations.endTime}
        duration={operations.duration}
        clinicSearchQuery={operations.clinicSearchQuery}
        dialogFacilitySearchQuery={operations.dialogFacilitySearchQuery}
        validationErrors={operations.validationErrors}
        isLoadingDialogFacilities={operations.isLoadingDialogFacilities}
        isCreatingSlots={operations.isCreatingSlots}
        onOpenChange={operations.setIsDialogOpen}
        onDialogClinicChange={operations.handleDialogClinicChange}
        onDialogFacilityChange={operations.setSelectedDialogFacility}
        onSelectedDaysChange={operations.setSelectedDays}
        onStartDateChange={operations.setStartDate}
        onEndDateChange={operations.setEndDate}
        onStartTimeChange={operations.setStartTime}
        onEndTimeChange={operations.setEndTime}
        onDurationChange={operations.setDuration}
        onClinicSearchChange={operations.setClinicsSearchQuery}
        onDialogFacilitySearchChange={operations.setDialogFacilitySearchQuery}
        onValidationErrorsClear={handleValidationErrorsClear}
        onConfirm={operations.handleConfirmSlot}
      />

      {/* Filter and Confirmation Dialogs */}
      <AppointmentSlotsDialogsComponent
        // Filter dialog state
        isFilterDialogOpen={operations.isFilterDialogOpen}
        filterStartDate={operations.filterStartDate}
        filterEndDate={operations.filterEndDate}
        filterStartTime={operations.filterStartTime}
        filterEndTime={operations.filterEndTime}

        // Confirmation dialog states
        deleteConfirmDialog={operations.deleteConfirmDialog}
        deleteAllConfirmDialog={operations.deleteAllConfirmDialog}

        // Filter dialog handlers
        onFilterDialogOpenChange={operations.setIsFilterDialogOpen}
        onFilterStartDateChange={operations.setFilterStartDate}
        onFilterEndDateChange={operations.setFilterEndDate}
        onFilterStartTimeChange={operations.setFilterStartTime}
        onFilterEndTimeChange={operations.setFilterEndTime}
        onFilterConfirm={operations.handleFilterConfirm}

        // Confirmation dialog handlers
        onDeleteConfirmDialogChange={operations.setDeleteConfirmDialog}
        onDeleteAllConfirmDialogChange={operations.setDeleteAllConfirmDialog}
        onConfirmDeleteAppointment={operations.confirmDeleteAppointment}
        onConfirmDeleteAll={operations.confirmDeleteAll}
      />
    </div>
  );
};

export default ManageClinicAppointmentSlotsRefactored;